import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GameRoom,
  LotteryInfo,
  LotteryJoinedPlayers,
  LotteryTurnNextTurn,
  LotteryFinishedState,
  LotteryAllNumberPicked,
  LotteryTurnNumbersPicked,
  LotteryTurnType,
} from '../types';
import { recordToArray } from '@utils/recordToArray';

export interface LotteryInfoGameRoomState {
  index: number;
  gameRoom: GameRoom;
  info: LotteryInfo;
  pickedNumbers: number[];
  availableNumbers: number[];
  myTurn?: boolean;
  gameStarted: boolean;
  picked: Record<number, boolean>;
  finishState?: LotteryFinishedState;
  gameTimer?: number;
  elapsedTime?: number;
  playerOrder?: number;
  players: number[];
  animationComplete: boolean;
  firstShowWinners: boolean;
  consecutiveWinNumbers: number[];
  lostConnection: boolean;
  isAutoplay: boolean;
  winnersScreen?: boolean;
  status?: 'insufficient_balance' | 'all_games_played' | 'in_progress' | 'stopped';
}

export interface LotteryGameRoomShort {
  gameRoomId: string;
  index: number;
  turnType: LotteryTurnType;
}

export interface LotterySliceState {
  error?: string;
  loading: boolean;
  info?: LotteryInfo;
  lotteryGameRoomsShort: LotteryGameRoomShort[];
  lotterySessions: Record<string, LotteryInfoGameRoomState>;
  lotteryAutoplayGameRoomId?: string;
  focusedGameRoomId?: string;
  noGamesAnimation: boolean;
  noFocused: boolean;
  autoplayGameRoomId?: string;
  nextAutoplayGameRoom?: LotteryInfoGameRoomState;
}

const initialState: LotterySliceState = {
  loading: true,
  noFocused: false,
  lotteryGameRoomsShort: [],
  lotterySessions: {},
  noGamesAnimation: true,
  autoplayGameRoomId: undefined,
  nextAutoplayGameRoom: undefined,
};

const possibleIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const getLotteryGameSession = (
  state: LotterySliceState,
  gameRoomId: string,
): LotteryInfoGameRoomState | undefined => {
  const session = state.lotterySessions[gameRoomId];
  if (session) return session;

  if (state.nextAutoplayGameRoom?.gameRoom.id === gameRoomId) {
    return state.nextAutoplayGameRoom;
  }

  return undefined;
};

const lotterySlice = createSlice({
  name: 'dice',
  initialState,
  reducers: {
    setLotteryError: (state, { payload }: PayloadAction<string | undefined>) => {
      state.error = payload;
    },
    setLotteryInfo: (state, { payload }: PayloadAction<LotteryInfo | undefined>) => {
      state.info = payload;
    },
    setLotteryLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    lotteryAgainGameFound: (
      state,
      { payload }: PayloadAction<{ gameRoom: GameRoom; againGameId: string }>,
    ) => {
      const { gameRoom, againGameId } = payload;
      const lotterySessionsArray = recordToArray(state.lotterySessions);

      const againGame = lotterySessionsArray.find((ls) => ls.id === againGameId);
      if (!againGame) return;

      const currentIndex = againGame.index;

      const info = state.info!;
      state.lotterySessions[gameRoom.id] = {
        index: currentIndex,
        gameRoom,
        pickedNumbers: [],
        availableNumbers: [],
        gameStarted: false,
        players: [],
        picked: {},
        info,
        animationComplete: false,
        firstShowWinners: true,
        consecutiveWinNumbers: [],
        lostConnection: false,
        isAutoplay: false,
      };
      state.lotteryGameRoomsShort.push({
        gameRoomId: gameRoom.id,
        index: currentIndex,
        turnType: gameRoom.settings.turnType,
      });

      delete state.lotterySessions[againGameId];
      state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
        (gameRoom) => gameRoom.gameRoomId !== againGameId,
      );

      if (state.focusedGameRoomId === againGameId) {
        state.focusedGameRoomId = gameRoom.id;
      }
    },

    lotteryGameFound: (
      state,
      {
        payload,
      }: PayloadAction<{
        gameRoom: GameRoom;
      }>,
    ) => {
      const { gameRoom } = payload;
      let currentIndex = -9;
      const lotterySessionsArray = recordToArray(state.lotterySessions);

      const foundGame = lotterySessionsArray.find((ls) => ls.id === gameRoom.id);

      if (foundGame) {
        const currentIndex = foundGame.index;

        delete state.lotterySessions[gameRoom.id];
        state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
          (gr) => gr.gameRoomId !== gameRoom.id,
        );
        const info = state.info!;
        state.lotterySessions[gameRoom.id] = {
          index: currentIndex,
          gameRoom,
          pickedNumbers: [],
          availableNumbers: [],
          gameStarted: false,
          players: [],
          picked: {},
          info,
          animationComplete: false,
          firstShowWinners: true,
          consecutiveWinNumbers: [],
          lostConnection: false,
          isAutoplay: false,
        };
        state.lotteryGameRoomsShort.push({
          gameRoomId: gameRoom.id,
          index: currentIndex,
          turnType: gameRoom.settings.turnType,
        });

        return;
      }

      for (const ind of possibleIndexes) {
        let indexFound = false;
        if (lotterySessionsArray.find((ls) => ls.index === ind)) indexFound = true;

        if (!indexFound) {
          currentIndex = ind;
          break;
        }
      }

      const info = state.info!;
      state.lotterySessions[gameRoom.id] = {
        index: currentIndex,
        gameRoom,
        pickedNumbers: [],
        availableNumbers: [],
        gameStarted: false,
        players: [],
        picked: {},
        info,
        animationComplete: false,
        firstShowWinners: true,
        consecutiveWinNumbers: [],
        lostConnection: false,
        isAutoplay: false,
      };
      state.lotteryGameRoomsShort.push({
        gameRoomId: gameRoom.id,
        index: currentIndex,
        turnType: gameRoom.settings.turnType,
      });
    },

    lotteryAutoplayGameInfo: (
      state,
      {
        payload,
      }: PayloadAction<{
        gameRoom: GameRoom;
        status: 'insufficient_balance' | 'all_games_played' | 'in_progress' | 'stopped';
      }>,
    ) => {
      const { gameRoom, status } = payload;
      const info = state.info!;

      const COMPLETED_STATUSES = ['insufficient_balance', 'all_games_played', 'stopped'];

      if (COMPLETED_STATUSES.includes(status)) {
        state.autoplayGameRoomId = undefined;
        state.nextAutoplayGameRoom = undefined;
        return;
      }

      if (state.autoplayGameRoomId) {
        state.nextAutoplayGameRoom = {
          index: 0,
          gameRoom,
          pickedNumbers: [],
          availableNumbers: [],
          gameStarted: gameRoom?.state?.isGameStarted ?? false,
          players: [],
          picked: {},
          info: info,
          animationComplete: false,
          firstShowWinners: true,
          consecutiveWinNumbers: [],
          lostConnection: false,
          isAutoplay: true,
          status: status,
        };

        return;
      }

      // Иначе запускаем новую игру сразу
      const usedIndexes = state.lotteryGameRoomsShort.map((gr) => gr.index);
      const currentIndex =
        possibleIndexes.find((i) => !usedIndexes.includes(i)) ?? possibleIndexes.length;

      state.autoplayGameRoomId = gameRoom.id;

      state.lotterySessions[gameRoom.id] = {
        index: currentIndex,
        gameRoom,
        pickedNumbers: [],
        availableNumbers: [],
        gameStarted: gameRoom?.state?.isGameStarted ?? false,
        players: [],
        picked: {},
        info: info,
        animationComplete: false,
        firstShowWinners: true,
        consecutiveWinNumbers: [],
        lostConnection: false,
        isAutoplay: true,
        status: status,
      };
      state.lotteryGameRoomsShort.push({
        gameRoomId: gameRoom.id,
        index: currentIndex,
        turnType: gameRoom.settings.turnType,
      });

      state.focusedGameRoomId = gameRoom.id;
    },

    lotteryAutoplayGameRemoveCurrent: (state) => {
      const currentId = state.autoplayGameRoomId;
      if (!currentId) return;

      const nextGame = state.nextAutoplayGameRoom;
      const autoplayStatus = state.lotterySessions[currentId].status;

      if (autoplayStatus === 'in_progress' && !nextGame) {
        return;
      }

      // Удаляем текущую игру
      delete state.lotterySessions[currentId];
      state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
        (gr) => gr.gameRoomId !== currentId,
      );
      state.focusedGameRoomId = undefined;
      state.autoplayGameRoomId = undefined;

      // Запускаем следующую, если она есть
      if (nextGame) {
        state.nextAutoplayGameRoom = undefined;

        const nextId = nextGame.gameRoom.id;
        state.autoplayGameRoomId = nextId;
        state.lotterySessions[nextId] = nextGame;

        state.lotteryGameRoomsShort.push({
          gameRoomId: nextId,
          index: nextGame.index,
          turnType: nextGame.gameRoom.settings.turnType,
        });

        state.focusedGameRoomId = nextId;
      }
    },

    lotteryAutoplayGameReplaceWithNext: (state) => {
      const currentId = state.autoplayGameRoomId;
      const nextGame = state.nextAutoplayGameRoom;

      if (!currentId) return;

      if (!nextGame) {
        delete state.lotterySessions[currentId];
        state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
          (gr) => gr.gameRoomId !== currentId,
        );

        state.autoplayGameRoomId = undefined;
        state.focusedGameRoomId = undefined;

        return;
      }

      delete state.lotterySessions[currentId];
      state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
        (gr) => gr.gameRoomId !== currentId,
      );

      const nextId = nextGame.gameRoom.id;
      state.autoplayGameRoomId = nextId;
      state.lotterySessions[nextId] = nextGame;

      state.lotteryGameRoomsShort.push({
        gameRoomId: nextId,
        index: nextGame.index,
        turnType: nextGame.gameRoom.settings.turnType,
      });

      state.focusedGameRoomId = nextId;

      state.nextAutoplayGameRoom = undefined;
    },

    lotteryAutoplayGameClose: (state) => {
      const currentId = state.autoplayGameRoomId;

      if (!currentId) {
        state.nextAutoplayGameRoom = undefined;
        state.focusedGameRoomId = undefined;
        return;
      }

      delete state.lotterySessions[currentId];

      state.lotteryGameRoomsShort = state.lotteryGameRoomsShort.filter(
        (gr) => gr.gameRoomId !== currentId,
      );

      state.autoplayGameRoomId = undefined;
      state.nextAutoplayGameRoom = undefined;
      state.focusedGameRoomId = undefined;
    },

    setWinnersScreen: (
      state,
      { payload }: PayloadAction<{ gameId: string; value: boolean }>,
    ) => {
      const game = state.lotterySessions[payload.gameId];
      if (game) {
        game.winnersScreen = payload.value;
      }
    },

    lotteryPlayerJoined: (state, { payload }: PayloadAction<LotteryJoinedPlayers>) => {
      const game = getLotteryGameSession(state, payload.gameRoomId);
      if (!game) return;

      game.players = payload.players;
    },
    lotteryGameStarted: (state, { payload }: PayloadAction<GameRoom>) => {
      const game = getLotteryGameSession(state, payload.id);
      if (!game) return;

      game.gameStarted = true;
      game.gameRoom.state = payload.state;
    },
    setFocusedGameRoomId: (state, { payload }: PayloadAction<string>) => {
      const game = getLotteryGameSession(state, payload);
      if (!game) return;

      state.focusedGameRoomId = payload;
    },
    resetFocusedGameRoomId: (state) => {
      state.focusedGameRoomId = undefined;
    },
    lotteryGameFinished: (state, { payload }: PayloadAction<LotteryFinishedState>) => {
      const game = getLotteryGameSession(state, payload.gameRoomId);
      if (!game) return;

      game.finishState = payload;
      delete game.elapsedTime;
    },
    lotteryGameAnimationCompleted: (state, { payload }: PayloadAction<string>) => {
      const game = getLotteryGameSession(state, payload);
      if (!game) return;

      game.animationComplete = true;
    },
    lotteryAllNumberPicked: (
      state,
      { payload }: PayloadAction<LotteryAllNumberPicked>,
    ) => {
      const game = getLotteryGameSession(state, payload.gameRoomId);
      if (!game) return;

      game.picked[payload.pickedNumber] = payload.myPick;
    },
    lotteryTurnNumberPicked: (
      state,
      { payload }: PayloadAction<LotteryTurnNumbersPicked>,
    ) => {
      const game = getLotteryGameSession(state, payload.gameRoomId);
      if (!game) return;

      for (const n of payload.pickedNumbers) {
        game.picked[n] = payload.myPick;
      }
    },
    lotteryNextTurn: (state, { payload }: PayloadAction<LotteryTurnNextTurn>) => {
      const game = getLotteryGameSession(state, payload.gameRoomId);
      if (!game) return;

      game.myTurn = payload.myTurn;
      game.elapsedTime = 10;
      game.playerOrder = game.playerOrder ? game.playerOrder + 1 : 1;
    },
    exitLotteryGame: (state, { payload }: PayloadAction<string>) => {
      const game = state.lotterySessions[payload];

      if (!game) return;

      const games = Object.values(state.lotterySessions).sort(
        (a, b) => a.index - b.index,
      );
      const gameIds = Object.keys(state.lotterySessions);
      const currentIndex = gameIds.indexOf(payload);

      let nextGameId: string | undefined;

      if (currentIndex + 1 < gameIds.length) {
        nextGameId = gameIds[currentIndex + 1];
      } else if (currentIndex - 1 >= 0) {
        nextGameId = gameIds[currentIndex - 1];
      }

      if (state.focusedGameRoomId === state.autoplayGameRoomId) {
        state.autoplayGameRoomId = undefined;
      }
      state.focusedGameRoomId = nextGameId;

      delete state.lotterySessions[payload];

      const updatedGamesRoomsShort: LotteryGameRoomShort[] = [];

      let index = 1;
      games.forEach((game) => {
        if (game.gameRoom.id === payload) return;

        state.lotterySessions[game.gameRoom.id].index = index;
        updatedGamesRoomsShort.push({
          gameRoomId: game.gameRoom.id,
          index,
          turnType: game.gameRoom.settings.turnType,
        });
        index++;
      });

      state.lotteryGameRoomsShort = updatedGamesRoomsShort;
    },
    exitAllGame: (state) => {
      state.lotteryGameRoomsShort = [];
      state.lotterySessions = {};
    },
    setLotteryTimer: (
      state,
      { payload }: PayloadAction<{ gameId: string; timer: number | undefined }>,
    ) => {
      const game = state.lotterySessions[payload.gameId];
      if (game) {
        if (payload.timer !== undefined) {
          game.gameTimer = payload.timer;
          game.elapsedTime = payload.timer;
        } else {
          delete game.gameTimer;
          delete game.elapsedTime;
        }
      }
    },
    setElapsedTime: (
      state,
      { payload }: PayloadAction<{ gameId: string; elapsedTime: number }>,
    ) => {
      const game = state.lotterySessions[payload.gameId];
      if (game) {
        game.elapsedTime = payload.elapsedTime;
      }
    },

    setNoGamesAnimation: (state, action: PayloadAction<boolean>) => {
      state.noGamesAnimation = action.payload;
    },
    setFirstShowWinners: (
      state,
      { payload }: PayloadAction<{ gameId: string; firstShow: boolean }>,
    ) => {
      const game = state.lotterySessions[payload.gameId];
      if (game) game.firstShowWinners = payload.firstShow;
    },
    addConsecutiveWinNumbers: (
      state,
      { payload }: PayloadAction<{ gameId: string; wnNumber: number }>,
    ) => {
      const game = state.lotterySessions[payload.gameId];
      if (game) game.consecutiveWinNumbers.push(payload.wnNumber);
    },
    setGameLostConnection: (state) => {
      Object.values(state.lotterySessions).forEach((game) => {
        state.lotterySessions[game.gameRoom.id].lostConnection = true;
        state.lotterySessions[game.gameRoom.id].animationComplete = true;
      });
    },
  },
});

export const {
  exitAllGame,
  setLotteryInfo,
  setElapsedTime,
  setLotteryTimer,
  setLotteryError,
  exitLotteryGame,
  lotteryNextTurn,
  lotteryGameFound,
  lotteryAutoplayGameInfo,
  lotteryAutoplayGameRemoveCurrent,
  lotteryAutoplayGameReplaceWithNext,
  lotteryAutoplayGameClose,
  setLotteryLoading,
  lotteryGameStarted,
  lotteryGameFinished,
  setWinnersScreen,
  lotteryPlayerJoined,
  setNoGamesAnimation,
  setFirstShowWinners,
  setFocusedGameRoomId,
  lotteryAgainGameFound,
  setGameLostConnection,
  resetFocusedGameRoomId,
  lotteryAllNumberPicked,
  lotteryTurnNumberPicked,
  addConsecutiveWinNumbers,
  lotteryGameAnimationCompleted,
} = lotterySlice.actions;

export default lotterySlice.reducer;
